import * as React from "react";
import { NavRail, RailNavButton, AikoMark } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/** Inline SVG glyphs — the rail keeps the app's inline-icon convention (no lucide). */
const glyph = (d: string) => () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);
const HomeGlyph = glyph("M3 11l9-8 9 8M5 10v10h14V10");
const StudioGlyph = glyph("M4 4h16v16H4zM4 9h16M9 9v11");
const AgentGlyph = glyph("M12 3a4 4 0 014 4v2a4 4 0 11-8 0V7a4 4 0 014-4zM5 21a7 7 0 0114 0");
const ConnectGlyph = glyph("M8 12h8M12 8v8M5 12a7 7 0 0114 0z");
const GovernGlyph = glyph("M12 3l8 4v5c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V7z");

const ITEMS = [
  { id: "home", icon: <HomeGlyph />, label: "Home" },
  { id: "studios", icon: <StudioGlyph />, label: "Studios" },
  { id: "agents", icon: <AgentGlyph />, label: "Agents" },
  { id: "connect", icon: <ConnectGlyph />, label: "Connect" },
  { id: "govern", icon: <GovernGlyph />, label: "Govern" },
];

/** A representative rail. `activeId` drives the active button variant. */
function DemoRail({
  activeId,
  magnify = true,
  compact = false,
  themeToggle = true,
}: {
  activeId: string;
  magnify?: boolean;
  compact?: boolean;
  themeToggle?: boolean;
}) {
  return (
    <NavRail
      brand={<AikoMark size={compact ? "sm" : "md"} />}
      magnify={magnify}
      forceCompact={compact}
      themeToggle={themeToggle}
    >
      {ITEMS.map((it) => (
        <RailNavButton
          key={it.id}
          icon={it.icon}
          label={it.label}
          // Icon-only rail (the activity-bar pattern). The 72px column has no
          // room for an inline label: the "rest" default flex-squeezes it to
          // nothing, and the "hover" reveal can't escape the `w-full` button as
          // a floating pill (tracked as a RailNavButton follow-up — it needs an
          // absolute-positioned label to break out of the flex flow). So the
          // name lives in the a11y tree (sr-only) for a tooltip / screen reader.
          labelMode="tooltip"
          variant={it.id === activeId ? "active" : "inactive"}
          size={compact ? "compact" : "default"}
        />
      ))}
    </NavRail>
  );
}

export function NavRailDemo() {
  const [activeId, setActiveId] = React.useState("home");

  return (
    // No local ThemeProvider: the rail's theme-toggle foot reads the
    // playground's root provider, so pressing it switches the real page theme.
    // A demo-scoped provider could never have isolated it — ThemeProvider
    // writes the light/dark class onto <html>, so a second one silently
    // overrode the page's own theme for as long as this demo was mounted.
    <Section
      id="nav-rail"
      title="NavRail"
      description="The Onyx app-shell left rail: a <nav> landmark with the AikoMark anchored at the top over a hairline divider, an icon-only RailNavButton stack, a flex spacer, and a theme-toggle foot. Owns ONE shared cursor MotionValue and a single pointer listener, published to opted-in tiles through NavRailMagnifyContext (restrained dock-magnify: only the hovered tile lifts to ~1.15 on a spring — neighbours stay 1.0). Icon-only by design (the activity-bar pattern): each destination name lives in the a11y tree for a tooltip / screen reader. The active tile carries a continuous accent keyline (an nx-rim ring on the hex-notch silhouette, so it traces every corner without clipping). Tokenised against the unified rail vars (--surface / --hairline / --forest / --ink) with lib fallbacks. Drops into AppShell's `rail` slot."
    >
      <VariantBlock
        label="At rest"
        caption="Default 72px rail · AikoMark brand · 5 destinations · theme-toggle foot. Hover a tile to feel the proximity magnify; the active tile carries the accent keyline."
      >
        <div className="flex h-[520px] overflow-visible rounded-[14px] border border-border">
          <DemoRail activeId={activeId} />
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <span>
                Active destination:{" "}
                <strong className="text-foreground">
                  {ITEMS.find((i) => i.id === activeId)?.label}
                </strong>
              </span>
              <div className="flex flex-wrap justify-center gap-2">
                {ITEMS.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setActiveId(it.id)}
                    className="rounded-full border border-border px-3 py-1 text-xs hover:border-accent hover:text-accent"
                  >
                    Set {it.label} active
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Representative state — Studios active"
        caption="The active button variant marks the current route."
      >
        <div className="flex h-[420px] overflow-visible rounded-[14px] border border-border">
          <DemoRail activeId="studios" />
          <div className="flex-1 p-6 text-sm text-muted-foreground">
            Studios surface would render here.
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Magnify off"
        caption="The rail stops publishing the cursor MotionValue — the calm, no-dock variant."
      >
        <div className="flex h-[420px] overflow-visible rounded-[14px] border border-border">
          <DemoRail activeId="home" magnify={false} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Compact (≤900px)"
        caption="48px rail — the same structure, tighter tiles."
      >
        <div className="flex h-[360px] overflow-visible rounded-[14px] border border-border">
          <DemoRail activeId="home" compact />
        </div>
      </VariantBlock>
    </Section>
  );
}
